import React, { FC, useState } from "react";
import { nb } from "date-fns/locale";

import { capitalize } from "../Helpers";

import { FormStateType } from "../Helpers/formTypes";
import { SummaryPart } from "./SummaryPart";
import { useLabels } from "../Hooks/useLabels";
import { CancellationMessage } from "../CancellationMessage/CancellationMessage";
import { RootDataType } from "../Helpers/interfaces";
import { PricingSummaryModal } from "../FormElements/PopUps/PricingSummaryModal";
import {
  calculateTotalPrice,
  getBaseEndPrice,
} from "../Helpers/totalPriceCalculator";
import { PriceDetailsButton } from "../PriceDetailsButton/PriceDetailsButton";
import { format } from "date-fns";
import { findRelatedSetupForAppointment } from "../Helpers/relatedServiceHelper";

type SummaryProps = {
  step: number;
  state: FormStateType;
  rootData: RootDataType;
  modal: any;
};

export const Summary: FC<SummaryProps> = ({ step, state, rootData, modal }) => {
  const { bookingLabels } = useLabels();
  const [showPriceSummaryModal, setShowPriceSummaryModal] = useState(false);

  const displayPriceInfo = !!state.pricingSummary.id;
  const displayPriceDetailsButton = state.service.disablePriceDetails == false;

  const displayAppointmentInfo =
    step >= 2 && !!state.appointment.serviceVariantId;

  const displayCancellationInfo = displayAppointmentInfo && !!state.appointment;

  const startDate = state.appointment.startTime;

  const title =
    bookingLabels?.stepsBookingLabels.secondStepLabels.summarySubHeading ??
    "Oppsummering dine valg";

  const isDoctorSelected = state.doctor.id && state.doctor.id != "-1";
  const doctor =
    isDoctorSelected && !state.appointment?.doctorId
      ? state.doctor
      : rootData.doctors?.filter((d) => d.id == state.appointment.doctorId)[0];
  const center =
    state.location.id &&
    state.location.id != "-1" &&
    state.location.isLocation == false &&
    !state.appointment?.centerId
      ? state.location
      : rootData.centers?.filter((c) => c.id == state.appointment.centerId)[0];

  const formattedAppointmentDate =
    startDate &&
    capitalize(
      format(startDate, "EEEE d'.' MMMM',' 'kl.' HH:mm", { locale: nb })
    );

  const serviceName = () => {
    if (state.appointment.serviceId) {
      return rootData.services.find(
        (s) => s.id === state.appointment.serviceId
      )!.bookingName;
    }
    return state.service.bookingName;
  };

  const specializationName = () => {
    return state.specialization.name;
  };

  const totalPrice = displayPriceInfo
    ? calculateTotalPrice(state.pricingSummary)
    : "";

  const baseEndPrice = getBaseEndPrice(state.pricingSummary);

  const relatedServiceSetup = findRelatedSetupForAppointment(
    state.relatedServiceSetups,
    state.appointment
  ).relatedServiceSetup;

  return (
    <div className="row mb-4">
      <div className="edge-to-edge-sm">
        <div className="card card-wings-sm mt-3">
          <div className="card-body px-2 px-sm-0 py-sm-3 fs-5">
            <h3 className="h5">{title}</h3>
            <dl className="mb-0">
              {displayAppointmentInfo && (
                <SummaryPart
                  label="Dato og tid"
                  content={formattedAppointmentDate}
                />
              )}
              <SummaryPart label="Tjeneste" content={specializationName()} />
              <SummaryPart
                label="Behandling"
                content={serviceName()}
                style={{
                  background: state.appointment?.isRelatedAppointment
                    ? relatedServiceSetup?.highlightColor
                    : undefined,
                }}
              />
              {center && <SummaryPart label="Sted" content={center.name} />}
              {doctor && (
                <SummaryPart
                  showJobTitle={state.service.showDoctorsJobTitle}
                  label="Behandler"
                  content={doctor.name}
                  additionalContent={!isDoctorSelected ? doctor.jobTitle : ""}
                />
              )}
              {displayPriceInfo && (
                <SummaryPart
                  label={
                    !!state.pricingSummary.campaignPrice
                      ? "Kampanjepris"
                      : "Pris"
                  }
                >
                  {!state.pricingSummary.listWithoutPrice && (
                    <>
                      {!!state.pricingSummary.campaignPrice && (
                        <span className="pricing-details-row__feeOriginPrice">
                          {baseEndPrice}
                        </span>
                      )}
                      {totalPrice.toLocaleString("nb")},-
                    </>
                  )}
                  {displayPriceDetailsButton && (
                    <PriceDetailsButton
                      service={state.service}
                      bookingLabels={bookingLabels!}
                      buttonCss={"ps-1 btn fw-normal fs-5 p-0 ps-1 btn-link"}
                      onClick={() => setShowPriceSummaryModal(true)}
                    />
                  )}
                </SummaryPart>
              )}
            </dl>
            {displayCancellationInfo && (
              <CancellationMessage
                message={state.cancellationDeadline?.message || ""}
              />
            )}
          </div>
        </div>
      </div>
      <PricingSummaryModal
        onClose={() => setShowPriceSummaryModal(false)}
        isOpen={showPriceSummaryModal}
        pricingSummary={state.pricingSummary}
        Modal={modal}
      />
    </div>
  );
};
